import interact from "interactjs";
import { RefObject, useEffect, useRef } from "react";
import {
    BLOCK_CLASS,
    HANDLE_LEFT_CLASS,
    HANDLE_RIGHT_CLASS,
    PAST_BLOCK_CLASS,
    columnAt,
    trackScale,
    DRAG_ACTIVE_CLASS,
    DROP_ACTIVE_CLASS,
    readPayload,
    SELECTION_CLASS,
    SOURCE_ACTIVE_CLASS,
    TRACK_CLASS,
} from "../components/plan-geometry";
import {
    columnLeft,
    columnsWidth,
    lastSlotStart,
    PlanGrid,
    slotsOfRange,
    slotsSpan,
    snapEnd,
    snapStart,
} from "../models/plan-scale";

export interface MoveIntent {
    fromRow: number;
    toRow: number;
    start: number;
    length: number;
    target: number;
    /** Ctrl/Cmd при отпускании — копирование вместо переноса */
    copy: boolean;
}

export interface ResizeIntent {
    rowIndex: number;
    start: number;
    length: number;
    /** Новая левая колонка блока — отличается от start, когда тянут левый край */
    newStart: number;
    newLength: number;
}

/** Куда встанет перетаскиваемый отрезок, если отпустить прямо сейчас */
export interface DropPreview {
    rowIndex: number;
    start: number;
    length: number;
}

export interface PlanInteractionsOptions {
    containerRef: RefObject<HTMLElement | null>;
    scrollRef: RefObject<HTMLElement | null>;
    columnsCount: number;
    /** Первый редактируемый день — левее него бросать нельзя */
    firstEditableColumn: number;
    /** Раскладка сетки: офсеты колонок и слоты, к которым липнут жесты */
    grid: PlanGrid;
    enabled: boolean;
    onMove: (intent: MoveIntent) => void;
    onResize: (intent: ResizeIntent) => void;
    onPreview: (preview: DropPreview | null) => void;
}

/**
 * Перетаскивание блоков (внутри строки и между сотрудниками) и растягивание за
 * правый край — на interact.js. Элементы находятся по CSS-классам (делегирование),
 * поэтому перерисовки React не требуют переподписки.
 *
 * Все размеры пересчитываются через реальную ширину дорожки, поэтому CSS-зум
 * страницы учитывается автоматически.
 */
export function usePlanInteractions(options: PlanInteractionsOptions) {
    const { containerRef, scrollRef, columnsCount, firstEditableColumn, grid, enabled, onMove, onResize, onPreview } =
        options;

    // колбэки и геометрию читаем через ref, чтобы не пересобирать interact на каждый
    // рендер — и чтобы смена масштаба сразу учитывалась уже подписанными жестами
    const handlers = useRef({ onMove, onResize, onPreview, columnsCount, firstEditableColumn, grid });
    handlers.current = { onMove, onResize, onPreview, columnsCount, firstEditableColumn, grid };

    useEffect(() => {
        const container = containerRef.current;

        if (!container || !enabled) {
            return;
        }

        const context = { context: container };
        const blockSelector = `.${BLOCK_CLASS}`;
        const pastBlockSelector = `.${PAST_BLOCK_CLASS}`;
        const selectionSelector = `.${SELECTION_CLASS}`;
        const trackSelector = `.${TRACK_CLASS}`;

        let hoveredTrack: HTMLElement | null = null;
        let sourceTrack: HTMLElement | null = null;
        let hoveredColumn = 0;
        let accumulatedY = 0;
        /** За какую по счёту ячейку отрезка его схватили */
        let grabOffset = 0;
        let dragLength = 0;
        /** Сколько ячеек сетки занимает отрезок — в недельном масштабе он едет неделями */
        let dragSlots = 0;
        /** Колонка, с которой отрезок начал переезд — от неё считается смещение */
        let dragStartColumn = 0;
        let lastPreview: DropPreview | null = null;

        const scaleOf = (track: Element | null) => (track ? trackScale(track, handlers.current.grid) : 1);

        /**
         * Левый край отрезка при текущем положении курсора: липнет к началу слота
         * (в недельном масштабе — к понедельнику), но не уезжает в прошлое.
         */
        const dropStart = () => {
            const grid = handlers.current.grid;

            // в неделях край считается ячейками: их длина в днях разная,
            // и вычитание дневной длины уводило бы отрезок не туда
            const limit =
                grid.scale === "week"
                    ? lastSlotStart(grid, dragSlots)
                    : handlers.current.columnsCount - dragLength;

            return Math.max(handlers.current.firstEditableColumn, snapStart(grid, Math.min(hoveredColumn - grabOffset, limit)));
        };

        /** Куда и на сколько встанет отрезок: в неделях — ровно на столько же ячеек */
        const dropRange = () => {
            const grid = handlers.current.grid;
            const start = dropStart();

            return grid.scale === "week" && dragSlots ? slotsSpan(grid, start, dragSlots) : { start, length: dragLength };
        };

        const publishPreview = () => {
            if (!hoveredTrack || !dragLength) {
                if (lastPreview) {
                    lastPreview = null;
                    handlers.current.onPreview(null);
                }
                return;
            }

            const rowIndex = Number(hoveredTrack.getAttribute("data-row"));

            if (!Number.isFinite(rowIndex)) {
                return;
            }

            const next: DropPreview = { rowIndex, ...dropRange() };

            if (
                lastPreview &&
                lastPreview.rowIndex === next.rowIndex &&
                lastPreview.start === next.start &&
                lastPreview.length === next.length
            ) {
                return;
            }

            lastPreview = next;
            handlers.current.onPreview(next);
        };

        const clearPreview = () => {
            dragLength = 0;
            dragSlots = 0;

            if (lastPreview) {
                lastPreview = null;
                handlers.current.onPreview(null);
            }
        };

        /**
         * Границы блока при растягивании считаем от колонки под курсором, а не от
         * прямоугольника элемента: мы сами округляем его размеры до целых колонок,
         * и interact продолжал бы отсчёт от неокруглённого значения, накапливая сдвиг.
         */
        const resizeGeometry = (target: HTMLElement, event: any) => {
            const track = target.closest(trackSelector) as HTMLElement | null;
            const payload = readPayload(target);

            if (!track || !payload) {
                return null;
            }

            const columnsCount = handlers.current.columnsCount;
            const pointer = columnAt(track, event.clientX, handlers.current.grid);
            const edges = event.edges || {};

            // курсор над пустой неделей — оставляем размеры как есть
            if (pointer < 0) {
                return { start: payload.start, length: payload.length };
            }

            const minColumn = handlers.current.firstEditableColumn;

            // края тоже липнут к слоту: в недельном масштабе блок меняется неделями
            if (edges.left) {
                const end = payload.start + payload.length;
                const start = Math.max(minColumn, Math.min(snapStart(handlers.current.grid, pointer), end - 1));

                return { start, length: end - start };
            }

            const end = Math.max(payload.start, Math.min(snapEnd(handlers.current.grid, pointer), columnsCount - 1));

            return { start: payload.start, length: end - payload.start + 1 };
        };

        // без enabled: true interact автопрокрутку не включает
        const autoScroll = scrollRef.current
            ? { container: scrollRef.current, margin: 40, speed: 600, enabled: true }
            : false;

        const dragConfig = {
            inertia: false,
            ignoreFrom: `.${HANDLE_LEFT_CLASS}, .${HANDLE_RIGHT_CLASS}`,
            autoScroll,
            listeners: {
                start(event: any) {
                    const target = event.target as HTMLElement;
                    const payload = readPayload(target);

                    accumulatedY = 0;
                    dragLength = payload ? payload.length : 0;
                    dragSlots = payload
                        ? slotsOfRange(handlers.current.grid, payload.start, payload.length).length
                        : 0;
                    dragStartColumn = payload ? payload.start : 0;

                    target.classList.add(DRAG_ACTIVE_CLASS);
                    target.style.willChange = "transform";

                    sourceTrack = target.closest(trackSelector) as HTMLElement | null;
                    hoveredTrack = sourceTrack;

                    if (sourceTrack) {
                        // поднимаем строку-источник, иначе следующие строки перекроют
                        // перетаскиваемый отрезок при движении вниз
                        sourceTrack.classList.add(SOURCE_ACTIVE_CLASS);
                    }

                    if (hoveredTrack) {
                        hoveredTrack.classList.add(DROP_ACTIVE_CLASS);
                        hoveredColumn = Math.max(0, columnAt(hoveredTrack, event.clientX, handlers.current.grid));

                        // запоминаем, за какую ячейку отрезка схватили: иначе при отпускании
                        // левый край встанет под курсор и отрезок уедет вправо
                        grabOffset = payload ? Math.max(0, hoveredColumn - payload.start) : 0;
                    } else {
                        grabOffset = 0;
                    }

                    publishPreview();
                },

                move(event: any) {
                    const target = event.target as HTMLElement;
                    const scale = scaleOf(hoveredTrack || target.closest(trackSelector));

                    accumulatedY += event.dy / scale;

                    // по горизонтали ведём отрезок сразу к месту приземления: ширина дня
                    // непостоянна, и снап «на целое число ячеек» дал бы разъезд с призраком
                    const grid = handlers.current.grid;
                    const snappedX = columnLeft(grid, dropStart()) - columnLeft(grid, dragStartColumn);

                    target.style.transform = `translate3d(${snappedX}px, ${accumulatedY}px, 0)`;
                },

                end(event: any) {
                    const target = event.target as HTMLElement;
                    const payload = readPayload(target);

                    target.classList.remove(DRAG_ACTIVE_CLASS);
                    target.style.transform = "";
                    target.style.willChange = "";

                    const track = hoveredTrack;
                    const start = dropStart();

                    hoveredTrack = null;

                    if (track) {
                        track.classList.remove(DROP_ACTIVE_CLASS);
                    }

                    if (sourceTrack) {
                        sourceTrack.classList.remove(SOURCE_ACTIVE_CLASS);
                        sourceTrack = null;
                    }

                    clearPreview();

                    if (!payload || !track) {
                        return;
                    }

                    const toRow = Number(track.getAttribute("data-row"));

                    if (!Number.isFinite(toRow)) {
                        return;
                    }

                    if (toRow === payload.rowIndex && start === payload.start) {
                        return;
                    }

                    handlers.current.onMove({
                        fromRow: payload.rowIndex,
                        toRow,
                        start: payload.start,
                        length: payload.length,
                        target: start,
                        copy: !!(event.ctrlKey || event.metaKey),
                    });
                },
            },
        };

        const resizeConfig = {
                autoScroll,
                // края определяем явными ручками: на стыке двух блоков граница одна,
                // и приоритет там у правого края левого блока
                edges: {
                    left: `.${HANDLE_LEFT_CLASS}`,
                    right: `.${HANDLE_RIGHT_CLASS}`,
                    top: false,
                    bottom: false,
                },
                inertia: false,
                listeners: {
                    start(event: any) {
                        (event.target as HTMLElement).classList.add(DRAG_ACTIVE_CLASS);
                    },

                    move(event: any) {
                        const target = event.target as HTMLElement;
                        const geometry = resizeGeometry(target, event);

                        if (!geometry) {
                            return;
                        }

                        target.style.left = `${columnLeft(handlers.current.grid, geometry.start)}px`;
                        target.style.width = `${columnsWidth(handlers.current.grid, geometry.start, geometry.length)}px`;
                    },

                    end(event: any) {
                        const target = event.target as HTMLElement;
                        const payload = readPayload(target);
                        const geometry = resizeGeometry(target, event);

                        target.classList.remove(DRAG_ACTIVE_CLASS);

                        // Возвращаем исходные значения, а не пустые: left/width задаёт React,
                        // и после ручной правки он не восстановит их, если в новом рендере
                        // значение совпадёт с предыдущим — блок остался бы с left: auto.
                        if (payload) {
                            target.style.left = `${columnLeft(handlers.current.grid, payload.start)}px`;
                            target.style.width = `${columnsWidth(handlers.current.grid, payload.start, payload.length)}px`;
                        }

                        if (!payload || !geometry) {
                            return;
                        }

                        if (geometry.start === payload.start && geometry.length === payload.length) {
                            return;
                        }

                        handlers.current.onResize({
                            rowIndex: payload.rowIndex,
                            start: payload.start,
                            length: payload.length,
                            newStart: geometry.start,
                            newLength: geometry.length,
                        });
                    },
            },
        };

        // Перетаскивание и растягивание живут на ОДНОМ interactable: если развести их
        // по разным, оба увидят нажатие на ручке (она внутри блока) и draggable
        // перехватит жест — блок начнёт ехать вместо того, чтобы растягиваться.
        const blocks = interact(blockSelector, context).draggable(dragConfig).resizable(resizeConfig);

        // у прошлых блоков перетаскивания нет — только продление вправо
        const pastBlocks = interact(pastBlockSelector, context).resizable(resizeConfig);

        const selections = interact(selectionSelector, context).draggable(dragConfig);

        const tracks = interact(trackSelector, context).dropzone({
            accept: `${blockSelector}, ${selectionSelector}`,
            overlap: "pointer",
            ondragenter(event: any) {
                if (hoveredTrack && hoveredTrack !== event.target) {
                    hoveredTrack.classList.remove(DROP_ACTIVE_CLASS);
                }

                hoveredTrack = event.target as HTMLElement;
                hoveredTrack.classList.add(DROP_ACTIVE_CLASS);
                publishPreview();
            },
            ondragleave(event: any) {
                (event.target as HTMLElement).classList.remove(DROP_ACTIVE_CLASS);
            },
            ondropdeactivate(event: any) {
                (event.target as HTMLElement).classList.remove(DROP_ACTIVE_CLASS);
            },
        });

        // dropzone сообщает только строку — колонку под курсором держим сами
        const trackPointer = (event: PointerEvent) => {
            if (!hoveredTrack) {
                return;
            }

            const column = columnAt(hoveredTrack, event.clientX, handlers.current.grid);

            // над неделей без рабочих дней приземляться некуда — держим прошлую позицию
            if (column < 0) {
                return;
            }

            hoveredColumn = column;
            publishPreview();
        };

        document.addEventListener("pointermove", trackPointer, true);

        return () => {
            document.removeEventListener("pointermove", trackPointer, true);
            blocks.unset();
            pastBlocks.unset();
            selections.unset();
            tracks.unset();
        };
    }, [containerRef, scrollRef, enabled]);
}
